<script>
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkButtonValidation from '@components/v2/general/AkButtonValidation';
import AkDropdown from '@components/v2/input/AkDropdown';
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import AkInputText from '@components/v2/input/AkInputText';

/* MIXINS */
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import otaTokenPlatformConst from '@mixins/const/otaTokenPlatformConst';
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';

/* SERVICES */
import otaTokenService from '@services/otaTokenService';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';

export default {
  components: {
    AkButtonValidationV2,
    AkButtonValidation,
    AkDropdown,
    AkFormSubmitted,
    AkInputText,
  },
  mixins: [loaderMixin, messageMixin, otaTokenPlatformConst, randomRef, utilsMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  emits: ['create-done'],
  props: {
    platform: {
      default: undefined,
    },
  },
  data() {
    return {
      messages: [],
      submitted: false,
      otaToken: {
        platform: this.platform,
      },
    };
  },
  validations() {
    return {
      otaToken: {
        platform: {required},
        label: {required},
        code: {required},
      },
    };
  },
  computed: {
    platformLabel() {
      return this.displayForOtaTokenPlatformConst(this.otaToken.platform);
    },
    refreshTokenLabel() {
      if (this.otaToken.platform === 'smily') return 'Clé API Smily';
      return 'Refresh Token OAuth';
    },
    hrefAirbnb() {
      const clientId = import.meta.env.VUE_APP_AIRBNB_CLIENT_ID;
      const redirectUri = encodeURIComponent(import.meta.env.VUE_APP_AIRBNB_REDIRECT_URI);
      // https%3A%2F%2Ftest-mobile.guest-adom-app.com
      return `https://www.airbnb.com/oauth2/auth?client_id=${clientId}&redirect_uri=${redirectUri}%2FcodeOTA&scope=property_management%20messages_read%20messages_write`;
    },
    hrefSmily() {
      const clientId = import.meta.env.VUE_APP_SMILY_CLIENT_ID;
      return `https://www.bookingsync.com/oauth/authorize?client_id=${clientId}&redirect_uri=urn:ietf:wg:oauth:2.0:oob&response_type=code&scope=applications_read%20bookings_write_owned%20bookings_read%20bookings_write%20clients_read%20clients_write%20inbox_read%20inbox_write%20inquiries_write_owned%20inquiries_read%20inquiries_write%20payments_write_owned%20payments_read%20payments_write%20preferences_general_settings_read%20preferences_general_settings_write%20preferences_payments_read%20preferences_payments_write%20rates_read%20rates_write%20rentals_read%20rentals_write%20reviews_read%20reviews_write%20webhooks_read%20webhooks_write%20webhooks_write_owned`;
    },
  },
  methods: {
    addSubError(content) {
      this.messages = [];
      this.messages.push({
        id: this.generateUUID(),
        closable: true,
        severity: 'error',
        content: content,
        life: 10000,
        icon: 'ga-icon ga-filled_cross text-3xl text-white',
      });
    },
    create() {
      this.messages = [];
      this.$refs.btnCreate.load();
      this.submitted = true;
      this.v$.$touch();
      if (this.v$.$error) {
        this.$refs.btnCreate.init();
        return;
      }
      otaTokenService
        .create(this.otaToken)
        .then(() => {
          this.$refs.btnCreate.init();
          this.success('Le compte a bien été ajouté');
          this.submitted = false;
          this.otaToken = {platform: this.platform};
          this.$emit('create-done');
        })
        .catch((e) => {
          this.$refs.btnCreate.init();
          this.addSubError(this.msgForErr(this.$t, e));
          this.submitted = false;
        });
    },
  },
};
</script>

<template>
  <Message
    class="mb-2"
    v-for="msg of messages"
    :key="msg.id"
    :closable="msg.closable"
    :icon="msg.icon"
    :severity="msg.severity"
    :life="msg.life">
    {{ msg.content }}
  </Message>
  <AkFormSubmitted :submitted="submitted" reference="form" @submit="create()">
    <div class="flex flex-col gap-2">
      <AkDropdown
        v-if="!platform"
        v-model="otaToken.platform"
        label="Plateforme OTA"
        :options="otaTokenPlatformConst"
        :validator="v$.otaToken.platform" />
      <div v-else class="flex items-center gap-2 mb-1">
        <span class="text-grey-500 text-sm">Plateforme</span>
        <span class="font-medium text-grey-900">{{ platformLabel }}</span>
      </div>
      <template v-if="otaToken.platform === 'airbnb'">
        <span>Cliquer sur le lien pour récuperer le code de vérification</span>
        <a :href="hrefAirbnb" target="_blank" class="btn-secondary">Récupérer le code</a>
      </template>

      <template v-if="otaToken.platform === 'smily'">
        <span>Cliquer sur le lien pour récuperer le code de vérification</span>
        <a :href="hrefSmily" target="_blank" class="btn-secondary">Récupérer le code</a>
      </template>

      <AkInputText v-model="otaToken.label" label="Libellé du compte" :validator="v$.otaToken.label" />
      <AkInputText v-model="otaToken.code" label="Code de vérification" :validator="v$.otaToken.code" />
    </div>
    <div class="mt-2 flex justify-end">
      <AkButtonValidationV2 :little="false" ref="btnCreate" @action="create" label="Ajouter" />
    </div>
  </AkFormSubmitted>
</template>
